import React, { useRef, useState } from 'react';
import { Row, Col } from 'antd';
import { I18n } from '@aws-amplify/core';

import { Title } from '@/components/Typography';
import { ModalBorder } from '@/components/ui';
import { PDFSectionsResolution886 } from '@/components/views/Resolution886/enums';
import Loading from '@/components/views/Resolution886/components/LoadingAnimation/Loading';
import LoadingSuccess from '@/components/views/Resolution886/components/LoadingAnimation/LoadingSuccess';
import type { Key } from 'rc-tree/lib/interface';
import { CustomButton } from '@/components/ui/Buttons/CustomButton/styles';
import * as S from './styles';

type MultipleCheckbox =
	| Key[]
	| {
			checked: Key[];
			halfChecked: Key[];
	  };

interface Resolution886PdfModalProps {
	open?: boolean;
	loading?: boolean;
	onShowFieldSelection?: boolean;
	onSubmit?: (values: any) => void;
	onClose?: () => void;
}

export function ModalPdf({
	open = false,
	loading = false,
	onShowFieldSelection = false,
	onSubmit,
	onClose
}: Readonly<Resolution886PdfModalProps>) {
	const initialValues: PDFSectionsResolution886[] = [
		PDFSectionsResolution886.IDENTIFICATION_RISK_FACTORS,
		PDFSectionsResolution886.IDENTIFICATION_CORRECTIVE_PREVENTIVE_MEASURES,
		PDFSectionsResolution886.TASK_EVALUATIONS,
		PDFSectionsResolution886.TASK_INITIAL_EVALUATION_RISK_FACTORS,
		PDFSectionsResolution886.TASK_RISK_EVALUATION,
		PDFSectionsResolution886.TASK_ACTION_PLAN,
		PDFSectionsResolution886.FOLLOW_UP_PREVENTIVE_MEASURES,
		PDFSectionsResolution886.COMMENT
	];

	const [values, setCheckBox] = useState<MultipleCheckbox>(initialValues);
	const [showSuccess, setShowSuccess] = useState<boolean>(false);
	const timerRef = useRef<NodeJS.Timer>();

	const modalTitle = onShowFieldSelection
		? I18n.get('')
		: I18n.get('Select the items you want to display in your document');

	const treeData = [
		{
			selectable: false,
			key: PDFSectionsResolution886.IDENTIFICATION_RISK_FACTORS,
			style: { fontWeight: 400, fontSize: 16, lineHeight: 24, letterSpacing: -0.015 },
			title: I18n.get('Identification of risk factors')
		},
		{
			selectable: false,
			key: PDFSectionsResolution886.IDENTIFICATION_CORRECTIVE_PREVENTIVE_MEASURES,
			style: { fontWeight: 400, fontSize: 16, lineHeight: 24, letterSpacing: -0.015 },
			title: I18n.get('Identification of corrective and preventive measures')
		},
		{
			title: I18n.get('Task evaluations'),
			key: PDFSectionsResolution886.TASK_EVALUATIONS,
			selectable: false,
			style: { fontWeight: 400, fontSize: 16, lineHeight: 24, letterSpacing: -0.015 },
			children: [
				{
					selectable: false,
					key: PDFSectionsResolution886.TASK_INITIAL_EVALUATION_RISK_FACTORS,
					style: { fontWeight: 400, fontSize: 16 },
					title: I18n.get('Initial evaluation of risk factors')
				},
				{
					selectable: false,
					key: PDFSectionsResolution886.TASK_RISK_EVALUATION,
					style: { fontWeight: 400, fontSize: 16 },
					title: I18n.get('Risk evaluation')
				},
				{
					selectable: false,
					key: PDFSectionsResolution886.TASK_ACTION_PLAN,
					style: { fontWeight: 400, fontSize: 16 },
					title: I18n.get('Action plan')
				}
			]
		},
		{
			selectable: false,
			key: PDFSectionsResolution886.FOLLOW_UP_PREVENTIVE_MEASURES,
			style: { fontWeight: 400, fontSize: 16, lineHeight: 24, letterSpacing: -0.015 },
			title: I18n.get('Follow-up on preventive measures')
		},
		{
			selectable: false,
			key: PDFSectionsResolution886.COMMENT,
			style: { fontWeight: 400, fontSize: 16, lineHeight: 24, letterSpacing: -0.015 },
			title: I18n.get('Comment')
		}
	];

	function handleOnCancel(): void {
		onClose?.();
	}

	function handleOnCheckTree(checked: MultipleCheckbox): void {
		setCheckBox(checked);
	}

	async function handleOnSubmit(): Promise<void> {
		Promise.all([
			onSubmit?.({ pdf_custom: values }),
			new Promise(function (resolve) {
				setTimeout(() => {
					setShowSuccess(true);
					resolve(true);
				}, 100);
			})
		]);

		timerRef.current = setTimeout(() => {
			setShowSuccess(false);
		}, 2300);
	}

	return (
		<ModalBorder $borderRadius="30px" open={open} footer={false} onCancel={handleOnCancel}>
			<Row justify="center" gutter={[0, 10]} style={{ marginTop: '0.5rem' }} hidden={loading || showSuccess}>
				<Col style={{ textAlign: 'center' }} span={20}>
					<Title level={3}>PDF</Title>
					<S.CustomParagraph>{modalTitle}</S.CustomParagraph>
				</Col>
				{!onShowFieldSelection && (
					<Col span={21}>
						<S.CustomTree
							checkable
							defaultExpandAll
							treeData={treeData}
							disabled={loading}
							defaultCheckedKeys={initialValues}
							onCheck={handleOnCheckTree}
						/>
					</Col>
				)}
				<Col>
					<CustomButton size="large" onClick={handleOnCancel} style={{ margin: '1rem 2rem 0 0' }}>
						{I18n.get('Cancel')}
					</CustomButton>
					<CustomButton loading={loading} size="large" onClick={handleOnSubmit} type="primary">
						{I18n.get('Download')}
					</CustomButton>
				</Col>
			</Row>
			{loading && <Loading />}
			{!loading && showSuccess && <LoadingSuccess />}
		</ModalBorder>
	);
}
